import { c as _c } from "react/compiler-runtime";
import { postHostedUserConsent } from '@onefootprint/fetch-hosted';
import type { RequirementConfirmations } from '@onefootprint/request-types';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useRouter } from '@tanstack/react-router';
import { useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { getConsentContent } from '@/idv2/components/consent-footer';
import { useRequiredConsent } from '@/idv2/features/app-shell/hooks/use-required-consent';
import { type IDV2Store, StoreContext, setExternalBack, setIdv2StoreData } from '@/idv2/features/idv-to-idv2';
import { Route as RootRoute } from '@/idv2/routes/__root';
import { getLogger } from '@/idv2/utils/logger';
import { getBackQueryKey, getProceedQueryKey } from '@/idv2/utils/proceed-query';
import { trackPreviousRouteOutcome } from '@/idv2/utils/route-tracking';
const {
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'use-app-navigate'
});
const IDENTIFY_BACK_TARGETS: Record<string, string> = {
  '/identify/phone': '/identify/email',
  '/identify/kba': '/identify/login',
  '/identify/otp/sms': '/identify/phone',
  '/identify/otp/email': '/identify/email'
};
type NavigationOptions = {
  /** When selecting branch outcomes in sandbox, the successor ID to navigate to. */
  chosenSuccessorId?: string;
  confirmations?: RequirementConfirmations;
};
export const useAppNavigate = () => {
  const $ = _c(29);
  const navigate = useNavigate();
  const location = useLocation();
  const router = useRouter();
  const store = useContext(StoreContext);
  const queryClient = useQueryClient();
  const requiredConsent = useRequiredConsent();
  const {
    t
  } = useTranslation("idv2");
  if (!store) {
    throw new Error("useIdv2 must be used within Idv2Provider");
  }
  const {
    onClose
  } = useStore(store, useShallow(_temp));
  const {
    onboardingSessionToken
  } = RootRoute.useRouteContext();
  let t0;
  if ($[0] !== location.pathname || $[1] !== navigate || $[2] !== onboardingSessionToken || $[3] !== queryClient || $[4] !== router.latestLocation.search) {
    t0 = async (direction, options) => {
      const currentPath = location.pathname;
      if (currentPath.startsWith("/identify")) {
        if (direction === "back") {
          const backTarget = IDENTIFY_BACK_TARGETS[currentPath];
          if (backTarget) {
            return navigate({
              to: backTarget,
              search: router.latestLocation.search
            });
          }
        }
        return navigate({
          to: "/identify"
        });
      }
      if (currentPath.startsWith("/onboarding")) {
        trackPreviousRouteOutcome(direction);
        await setIdv2StoreData({
          navigation: direction
        });
        await queryClient.invalidateQueries({
          type: "all",
          queryKey: direction === "forward" ? getProceedQueryKey() : getBackQueryKey(),
          refetchType: "none"
        });
        return navigate({
          to: "/onboarding/$token",
          params: {
            token: onboardingSessionToken
          },
          search: {
            confirmations: options?.confirmations,
            chosenSuccessorId: options?.chosenSuccessorId
          }
        });
      }
    };
    $[0] = location.pathname;
    $[1] = navigate;
    $[2] = onboardingSessionToken;
    $[3] = queryClient;
    $[4] = router.latestLocation.search;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  const navigateTo = t0;
  let t1;
  if ($[6] !== navigateTo || $[7] !== onboardingSessionToken || $[8] !== requiredConsent || $[9] !== t) {
    t1 = async args => {
      const content = getConsentContent(t, requiredConsent);
      await Promise.all(requiredConsent.map(consent => postHostedUserConsent({
        headers: {
          "X-Fp-Authorization": onboardingSessionToken
        },
        body: {
          content,
          kind: consent.kind
        }
      }).catch(error => logWarn(`Failed to record ${consent.kind} consent`, error))));
      await navigateTo("forward", args);
    };
    $[6] = navigateTo;
    $[7] = onboardingSessionToken;
    $[8] = requiredConsent;
    $[9] = t;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const forward = t1;
  let t2;
  if ($[11] !== navigateTo) {
    t2 = async () => {
      await navigateTo("back");
    };
    $[11] = navigateTo;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const back = t2;
  const {
    scope,
    onIdentifyDone
  } = RootRoute.useRouteContext();
  let t3;
  if ($[13] !== navigate || $[14] !== navigateTo || $[15] !== onIdentifyDone || $[16] !== scope) {
    t3 = validationToken => {
      onIdentifyDone?.(validationToken);
      if (scope === "auth") {
        return navigate({
          to: "/complete",
          search: {
            validationToken
          }
        });
      }
      return navigateTo("forward");
    };
    $[13] = navigate;
    $[14] = navigateTo;
    $[15] = onIdentifyDone;
    $[16] = scope;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const identifyComplete = t3;
  const currentPath_0 = location.pathname;
  let t4;
  if ($[18] !== currentPath_0) {
    t4 = currentPath_0.startsWith("/identify") && !!IDENTIFY_BACK_TARGETS[currentPath_0];
    $[18] = currentPath_0;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const identifyCanGoBack = t4;
  let t5;
  let t6;
  if ($[20] !== back) {
    t5 = () => {
      setExternalBack(back);
      return _temp2;
    };
    t6 = [back];
    $[20] = back;
    $[21] = t5;
    $[22] = t6;
  } else {
    t5 = $[21];
    t6 = $[22];
  }
  useEffect(t5, t6);
  let t7;
  if ($[23] !== back || $[24] !== forward || $[25] !== identifyCanGoBack || $[26] !== identifyComplete || $[27] !== onClose) {
    t7 = {
      forward,
      back,
      close: onClose,
      identifyComplete,
      identifyCanGoBack
    };
    $[23] = back;
    $[24] = forward;
    $[25] = identifyCanGoBack;
    $[26] = identifyComplete;
    $[27] = onClose;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  return t7;
};
function _temp(state) {
  return {
    onClose: state.onClose
  };
}
function _temp2() {
  return setExternalBack(undefined);
}